import { useEffect } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://dreamlinebridal.com';

const jsonLdData = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Dreamline Bridal',
    url: SITE_URL,
    description:
      'Dreamline Bridal creates custom luxury wedding gowns for the modern bride. Bespoke couture crafted by hand, delivered worldwide.',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/?s={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Dreamline Bridal',
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    email: 'hello@dreamlinebridal.com',
    description:
      'Custom luxury wedding gowns designed exclusively for each bride. Share your vision, consult with our designers, and receive a handcrafted bespoke gown delivered worldwide.',
    sameAs: [],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@dreamlinebridal.com',
      availableLanguage: 'English',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/#webpage`,
    url: SITE_URL,
    name: 'Dreamline Bridal | Custom Luxury Wedding Gowns — Bespoke Couture',
    description:
      'Dreamline Bridal creates custom luxury wedding gowns for the modern bride. Upload your inspiration, consult with our designers, and receive your dream dress — crafted by hand, delivered worldwide.',
    isPartOf: {
      '@id': `${SITE_URL}/#website`,
    },
    inLanguage: 'en',
    dateModified: '2026-03-24',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: SITE_URL,
        },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ClothingStore',
    name: 'Dreamline Bridal',
    description:
      'Custom luxury wedding gowns designed exclusively for each bride. Share your vision, get a free consultation, and receive a handcrafted bespoke gown delivered worldwide.',
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    email: 'hello@dreamlinebridal.com',
    priceRange: '$$$',
    currenciesAccepted: 'USD',
    paymentAccepted: 'Credit Card',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Wedding Gown Collections',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Product',
            name: 'Essential Custom Wedding Gown',
            description: 'Fully custom handmade wedding gown — bespoke fit, your design',
          },
          price: '4200',
          priceCurrency: 'USD',
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Product',
            name: 'Signature Custom Wedding Gown',
            description: 'Elevated bespoke gown with premium fabrics and intricate detailing',
          },
          price: '5400',
          priceCurrency: 'USD',
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Product',
            name: 'Couture Custom Wedding Gown',
            description: 'Full couture creation with hand-embroidery, rare materials, and unlimited revisions',
          },
          price: '6700',
          priceCurrency: 'USD',
        },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Bespoke Wedding Gown Design',
    name: 'Custom Wedding Gown Design & Creation',
    provider: {
      '@type': 'ClothingStore',
      name: 'Dreamline Bridal',
      url: SITE_URL,
    },
    description:
      'A fully personalised custom wedding gown design service. Upload your inspiration, receive a design sketch, choose your fabrics, and get a hand-crafted gown delivered to your door.',
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: '4200',
      highPrice: '6700',
      priceCurrency: 'USD',
    },
    areaServed: 'Worldwide',
    availableLanguage: 'English',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How does the custom wedding gown process work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Start with a free consultation — upload your inspiration images and our design team will reach out within 24 hours. We sketch your design, source premium fabrics, and craft your bespoke gown by hand, delivering it in approximately 5 months.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much does a custom wedding gown from Dreamline Bridal cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our custom wedding gowns start at $4,200 for the Essential package, $5,400 for the Signature package, and $6,700 for full Couture. All packages include a bespoke fit tailored to your exact measurements.',
        },
      },
      {
        '@type': 'Question',
        name: 'How long does it take to receive my custom bridal gown?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The average process takes approximately 5 months from consultation to delivery. We recommend booking at least 5 months before your wedding date.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do you ship internationally?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, Dreamline Bridal ships handcrafted wedding gowns worldwide. We work with brides across North America, Europe, and beyond.',
        },
      },
    ],
  },
];

export default function SEOJsonLd() {
  useEffect(() => {
    jsonLdData.forEach((data, i) => {
      const existing = document.getElementById(`jsonld-${i}`);
      if (existing) existing.remove();

      const script = document.createElement('script');
      script.id = `jsonld-${i}`;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });

    return () => {
      jsonLdData.forEach((_, i) => {
        const el = document.getElementById(`jsonld-${i}`);
        if (el) el.remove();
      });
    };
  }, []);

  return null;
}